import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Spinner,
  Button,
  Progress,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from "reactstrap";
import {
  getAllProjects,
  getProjectDetails,
  getTaskAuditLogs,
  getSubTaskAuditLogs,
  getSubTaskLogs,
  getTaskLogs,
  getUnreadCount
} from "../../Api/ApiUrlConst";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { format } from "date-fns";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  LabelList,
} from "recharts";
import { useAuth } from "../../Context/AuthContext";
import Comments from "../CommentComponent/Comments";
import classnames from "classnames";


interface SubTask {
  id: number;
  title: string;
  dueDate: string;
  status: string;
  priority: string;
  assignedToName?: string;
  percentCompleted: number;
  qcProgress: number;

  workHours?: number;
  completionDate: string | null;
  remarks?: string;
  issues?: string;
}

interface Task {
  id: number;
  title: string;
  projectId: string;
  assignedTo: string;
  dueDate: string;
  status: string;
  priority: string;
  percentCompleted: number;
  qcProgress: number;
  remarks?: string;
  completionDate?: string;
  issues?: string;
  subTasks: SubTask[];
}

interface Project {
  id: number;
  projectID: string;
  projectName: string;
  tasks?: Task[];
}

interface AuditLog {
  id: number;
  userId: string;
  userName: string;
  action: string;
  entity: string;
  entityId: string;
  timestamp: string;
  details: string;
  oldValue?: string;
  newValue?: string;
}

interface ChartDataPoint {
  timestamp: string;
  percentCompleted: number;
  details: string;
  updatedBy?: string;   // 👈 add this
}


const GetAllProjectsComponents: React.FC = () => {
  const { userID } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<{ [taskId: number]: boolean }>({});
  const [modalOpen, setModalOpen] = useState(false);

  // Progress history modal (task/subtask)
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [chartLoading, setChartLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("1");
  const [selectedEntity, setSelectedEntity] = useState<{
    type: "task" | "subtask";
    id: number;
    title?: string;
  } | null>(null);

  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);

  // NEW states for OverallLog only
  const [overallLogs, setOverallLogs] = useState<any[]>([]);
  const [overallStatusHistory, setOverallStatusHistory] = useState<any[]>([]);

  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  const loadUnreadCountFor = async (
    type: "project" | "task" | "subtask",
    id: number
  ) => {
    try {
      const count = await getUnreadCount(type, id);
      setUnreadCounts(prev => ({
        ...prev,
        [`${type}-${id}`]: count,
      }));
    } catch (err) {
      console.error("Failed unread count", err);
    }
  };


  const [commentPopup, setCommentPopup] = useState<{
    open: boolean;
    type: "project" | "task" | "subtask";
    id: number;
    title?: string;
  } | null>(null);


  const openComments = (
    type: "project" | "task" | "subtask",
    id: number,
    title?: string
  ) => {
    setCommentPopup({
      open: true,
      type,
      id,
      title
    });
  };


  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await getAllProjects();
      const proj = res.data?.$values || res.data || [];
      setProjects(proj);
    } catch (e) {
      console.error("Failed loading projects", e);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectDetails = async (project: Project) => {
    try {
      const res = await getProjectDetails(project.projectID);
      if (!res?.data) return;

      const tasksArray = res.data.tasks
        ? Array.isArray(res.data.tasks)
          ? res.data.tasks
          : res.data.tasks.$values || []
        : [];

      const normalizedTasks: Task[] = tasksArray.map((task: any) => {
        const subTasksArr = task.subTasks
          ? Array.isArray(task.subTasks)
            ? task.subTasks
            : task.subTasks.$values || []
          : [];
        return {
          id: task.id,
          title: task.description || task.title || "",
          projectId: task.projectId || "",
          assignedTo: task.assignedToName || task.assignedToId || "",
          dueDate: task.dueDate,
          status: task.status,
          completionDate: task.completionDate,
          priority: task.priority,
          percentCompleted: task.percentCompleted || 0,
          qcProgress: task.qcProgress || 0,
          remarks: task.remarks || "",
          issues: task.issues || "",
          subTasks: subTasksArr.map((sub: any) => ({
            id: sub.id,
            title: sub.description || sub.title || "",
            dueDate: sub.dueDate,
            status: sub.status,
            priority: sub.priority,
            assignedToName: sub.assignedToName || sub.assignedUserName || "",
            percentCompleted: sub.percentCompleted || 0,
            qcProgress: sub.qcProgress || 0,

            workHours: sub.workHours,
            completionDate: sub.completionDate || null,
            remarks: sub.remarks || "",
            issues: sub.issues || "",
          })),
        };
      });

      setSelectedProject({ ...project, tasks: normalizedTasks });
      setModalOpen(true);
    } catch (e) {
      console.error("Failed loading project tasks", e);
    }
  };

  const toggleTaskExpand = (taskId: number) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  // ---------------------------
  // Progress history (audit logs)
  // ---------------------------
  const loadAuditLogsForChart = async (entityType: "task" | "subtask", entityId: number) => {
    setChartLoading(true);
    try {
      let logs: AuditLog[] = [];
      if (entityType === "task") {
        const res = await getTaskAuditLogs(entityId);
        logs = res || [];
      } else {
        const res = await getSubTaskAuditLogs(entityId);
        logs = res || [];
      }

      setAuditLogs(logs);

      const rawPoints: ChartDataPoint[] = logs
        .filter((l) => l.action === "Update" || l.action === "Create")
        .map((log) => {
          let percentCompleted = 0;
          try {
            if (log.newValue) {
              const parsed = JSON.parse(log.newValue);
              // take either PascalCase or camelCase
              percentCompleted = parsed.PercentCompleted ?? parsed.percentCompleted ?? 0;
            }
          } catch {
            percentCompleted = 0;
          }

          // fallback parse from details text
          if ((!percentCompleted || isNaN(percentCompleted)) && log.details) {
            const m = log.details.match(/PercentCompleted:\s*(\d+)\s*→\s*(\d+)/);
            if (m) percentCompleted = Number(m[2]);
          }

          return {
            timestamp: new Date(log.timestamp).toISOString().split("T")[0],
            percentCompleted: Number(percentCompleted) || 0,
            details: log.details || "",
            updatedBy: log.userName || log.userId || "Unknown"
          };

        })
        .filter((d) => d.percentCompleted >= 0);


      // group by day and pick latest (simple)
      const grouped: Record<string, ChartDataPoint> = {};
      rawPoints.forEach((pt) => {
        const existing = grouped[pt.timestamp];
        if (!existing) grouped[pt.timestamp] = pt;
        else {
          // keep one with higher percent or later timestamp
          if (pt.percentCompleted >= existing.percentCompleted) grouped[pt.timestamp] = pt;
        }
      });

      const sorted = Object.values(grouped).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      // carry forward last known
      const filled: ChartDataPoint[] = [];
      let last = 0;
      sorted.forEach((s) => {
        if (s.percentCompleted === 0 && last > 0) s.percentCompleted = last;
        else last = s.percentCompleted;
        filled.push({
          ...s,
          timestamp: new Date(s.timestamp).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
        });
      });

      setChartData(filled);
    } catch (err) {
      console.error("Failed to load audit logs:", err);
      setChartData([]);
    } finally {
      setChartLoading(false);
    }
  };

  const statusHistory = auditLogs
    .filter((item) => item.details.includes("Status:"))
    .map((item) => {
      const match = item.details.match(/Status:\s*([^→]+)→\s*(.+)/);
      if (!match) return;

      return {
        rawTimestamp: item.timestamp,
        date: new Date(item.timestamp).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        time: new Date(item.timestamp).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        from: match[1].trim(),
        to: match[2].trim(),
        updatedBy: item.userName || item.userId // fallback
      };
    })
    .filter(
      (x): x is { rawTimestamp: string; date: string; time: string; from: string; to: string; updatedBy: string } =>
        Boolean(x)

    )
    .sort(
      (a, b) =>
        new Date(a.rawTimestamp).getTime() -
        new Date(b.rawTimestamp).getTime()
    );

  const handlePercentClick = (
    entityType: "task" | "subtask",
    entityId: number,
    title: string
  ) => {
    setSelectedEntity({ type: entityType, id: entityId, title });
    loadOverallLogsForChart(entityType, entityId);

    // Load status history (OverallLog)
    loadOverallStatusHistory(entityType, entityId);
    loadAuditLogsForChart(entityType, entityId);
    setChartModalOpen(true);
  };


  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload as ChartDataPoint;

    return (
      <div className="bg-white p-2 border rounded small shadow-sm">
        <div><strong>{label}</strong></div>

        {/* Percent / Status Change */}
        <div className="text-muted small">
          {data.details}
        </div>

        {/* USER NAME */}
        {data.updatedBy && (
          <div className="small mt-1" style={{ fontWeight: 600 }}>
            Updated By: {data.updatedBy}
          </div>
        )}
      </div>
    );
  };



  // ----------------------------------------------------------
  // NEW: Load Overall Logs (no effect on existing auditLogs)
  // ----------------------------------------------------------
  const loadOverallLogsForChart = async (
    entityType: "task" | "subtask",
    entityId: number
  ) => {
    setChartLoading(true);

    try {
      let logs: any[] = [];

      if (entityType === "task") {
        logs = await getTaskLogs(entityId);
      } else {
        logs = await getSubTaskLogs(entityId);
      }

      // Save ALL logs so UI table can show everything
      setOverallLogs(logs);

      // -----------------------------
      // Chart Only → Filter logs containing PercentCompleted
      // -----------------------------
      const percentLogs = logs.filter(
        (l) =>
          l.fieldName?.toLowerCase() === "percentcompleted" &&
          !isNaN(Number(l.newValue))
      );

      const rawPoints: ChartDataPoint[] = percentLogs.map((log) => ({
        timestamp: new Date(log.logTime).toISOString().split("T")[0],
        percentCompleted: Number(log.newValue ?? 0),
        details: `${log.oldValue ?? "-"} → ${log.newValue ?? "-"}`
      }));

      // Group by date → keep highest % in the same day
      const grouped: any = {};
      rawPoints.forEach((pt) => {
        if (!grouped[pt.timestamp] || pt.percentCompleted > grouped[pt.timestamp].percentCompleted) {
          grouped[pt.timestamp] = pt;
        }
      });

      const sorted = Object.values(grouped).sort(
        (a: any, b: any) =>
          new Date(a.timestamp).getTime() -
          new Date(b.timestamp).getTime()
      );

      setChartData(
        sorted.map((d: any) => ({
          ...d,
          timestamp: new Date(d.timestamp).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
        }))
      );
    } catch (err) {
      console.error("Failed to load overall logs:", err);
      setOverallLogs([]);
      setChartData([]);
    }

    setChartLoading(false);
  };


  // ------------------------------------------------------------------
  // NEW: Convert OverallLog → Status History table (separate state)
  // ------------------------------------------------------------------
  const loadOverallStatusHistory = async (
    entityType: "task" | "subtask",
    entityId: number
  ) => {
    try {
      let logs: any[] = [];

      if (entityType === "task") {
        const res = await getTaskLogs(entityId);
        logs = res ?? [];
      } else {
        const res = await getSubTaskLogs(entityId);
        logs = res ?? [];
      }

      const statusLogs = logs.filter(
        (l) =>
          l.fieldName?.toLowerCase() === "status" &&
          l.oldValue !== l.newValue
      );

      const formatted = statusLogs
        .map((log) => ({
          rawTimestamp: log.logTime,
          date: new Date(log.logTime).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          time: new Date(log.logTime).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
          from: log.oldValue ?? "-",
          to: log.newValue ?? "-",
          updatedBy: log.userName ?? "Unknown",
        }))
        .sort(
          (a, b) =>
            new Date(a.rawTimestamp).getTime() -
            new Date(b.rawTimestamp).getTime()
        );

      setOverallStatusHistory(formatted);
    } catch (err) {
      console.error("Failed to load overall status history:", err);
      setOverallStatusHistory([]);
    }
  };



  const statusColorMap: Record<string, string> = {
    'Yet to Start': 'status-yet-to-start',
    YetToStart: 'status-yet-to-start',

    'On Hold': 'status-on-hold',
    OnHold: 'status-on-hold',

    'Waiting for Input': 'status-waiting-input',
    WaitingForInput: 'status-waiting-input',

    'Modelling in Progress': 'status-modelling-progress',
    ModellingInProgress: 'status-modelling-progress',

    'Modelling ToDo': 'status-modelling-Todo',
    ModellingTodo: 'status-modelling-Todo',

    'Modelling Completed': 'status-modelling-completed',
    ModellingCompleted: 'status-modelling-completed',

    'Drafting in Progress': 'status-drafting-progress',
    DraftingInProgress: 'status-drafting-progress',

    'Drafting ToDo': 'status-drafting-Todo',
    DraftingTodo: 'status-drafting-Todo',

    'Drafting Completed': 'status-drafting-completed',
    DraftingCompleted: 'status-drafting-completed',

    'QC ToDo': 'status-qc-Todo',
    QCTodo: 'status-qc-Todo',

    'QC in Progress': 'status-qc-progress',
    QCInProgress: 'status-qc-progress',

    'QC Completed': 'status-qc-completed',
    QCCompleted: 'status-qc-completed',

    'Client Review in Progress': 'status-client-review-progress',
    ClientReviewInProgress: 'status-client-review-progress',

    'Client Review ToDo': 'status-client-review-Todo',
    ClientReviewTodo: 'status-client-review-Todo',



    'Client Review Completed': 'status-client-review-completed',
    ClientReviewCompleted: 'status-client-review-completed',

    'Revision in Progress': 'status-Revision-progress',
    RevisionInProgress: 'status-Revision-progress',

    'Revision ToDo': 'status-Revision-Todo',
    RevisionTodo: 'status-Revision-Todo',

    'Revision Completed': 'status-Revision-completed',
    RevisionCompleted: 'status-Revision-completed',


    'Final Correction in Progress': 'status-final-correction-progress',
    FinalCorrectionInProgress: 'status-final-correction-progress',

    'Final Correction ToDo': 'status-final-correction-Todo',
    FinalCorrectionTodo: 'status-final-correction-Todo',

    'Final Correction Completed': 'status-final-correction-completed',
    FinalCorrectionCompleted: 'status-final-correction-completed',

    'Not In Scope': 'status-Not-In-Scope',
    NotInScope: 'status-Not-In-Scope',

    Released: 'status-released',
    'Completed & Closed': 'status-completed',
    Completed: 'status-completed',

    Cancelled: 'status-cancelled',
    Issues: 'status-issues',

 CompletedAndReleased: "status-completedandreleased",


  NotApplicable1: "status-Drafting-Released",
  };
  const statusOptions = [
    { value: "YetToStart", label: "Yet to Start" },
    { value: "WaitingForInput", label: "Waiting for Input" },
    { value: "", label: "" },
    { value: "ModellingTodo", label: "Modelling ToDo" },
    { value: "ModellingInProgress", label: "Modelling in Progress" },
    { value: "ModellingCompleted", label: "Modelling Completed" },
    { value: "", label: "" },
    { value: "DraftingTodo", label: "Drafting ToDo" },
    { value: "DraftingInProgress", label: "Drafting in Progress" },
    { value: "DraftingCompleted", label: "Drafting Completed" },
    { value: "", label: "" },
    { value: "QCTodo", label: "QC ToDo" },
    { value: "QCInProgress", label: "QC in Progress" },
    { value: "QCCompleted", label: "QC Completed" },
    { value: "", label: "" },
    { value: "ClientReviewTodo", label: "Client Review ToDo" },
    { value: "ClientReviewInProgress", label: "Client Review in Progress" },
    { value: "ClientReviewCompleted", label: "Client Review Completed" },
    { value: "", label: "" },
    { value: "FinalCorrectionTodo", label: "Final Correction ToDo" },
    { value: "FinalCorrectionInProgress", label: "Final Correction in Progress" },
    { value: "FinalCorrectionCompleted", label: "Final Correction Completed" },
    { value: "", label: "" },
    { value: "RevisionTodo", label: "Revision ToDo" },
    { value: "RevisionInProgress", label: "Revision in Progress" },
    { value: "RevisionCompleted", label: "Revision Completed" },
    { value: "", label: "" },
    { value: "Cancelled", label: "Cancelled" },
    { value: 'Issues', label: 'Issues' },
    { value: "OnHold", label: "On Hold" },
    { value: "Released", label: "Released" },
    { value: "NotInScope", label: "Not In Scope" },
    { value: "Completed", label: "Ready for Release" },
  ];

  const getStatusLabel = (value: string) => {
    const match = statusOptions.find(s => s.value === value);
    return match ? match.label : value;
  };

  const toggle = (tab: "1" | "2") => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  useEffect(() => {
    if (!selectedProject || !selectedProject.tasks) return;

    selectedProject.tasks.forEach(task => {
      loadUnreadCountFor("task", task.id);

      task.subTasks?.forEach(sub => {
        loadUnreadCountFor("subtask", sub.id);
      });
    });
  }, [selectedProject]);



  // ---------------------------
  // Render
  // ---------------------------
  return (
    <Container fluid className="py-3">
      <Row>
        <Col md={3}>
          <Card className="shadow-sm" style={{ width: "250px" }}>
            <CardHeader className="bg-primary text-white">Projects</CardHeader>
            <CardBody style={{ maxHeight: "80vh", overflowY: "auto" }}>
              {loading ? (
                <Spinner color="primary" />
              ) : (
                <ul className="list-group">
                  {projects.map((p) => (
                    <li
                      key={p.id}
                      className="list-group-item list-group-item-action"
                      onClick={() => loadProjectDetails(p)}
                      style={{ cursor: "pointer" }}
                    >
                      <strong>{p.projectID}</strong> - {p.projectName}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </Col>


      </Row>

      {/* Project details modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered size="lg" fullscreen>
        <ModalHeader toggle={() => setModalOpen(false)} className="bg-primary text-white">
          {selectedProject?.projectName || "Project Details"}
        </ModalHeader>
        <ModalBody className="bg-light">
          {!selectedProject ? (
            <p className="text-center text-muted">Click a project to view tasks & subtasks.</p>
          ) : selectedProject.tasks && selectedProject.tasks.length > 0 ? (
            <Table responsive bordered hover className="align-middle shadow-sm rounded bg-white">
              <thead className="table-primary">
                <tr>
                  <th></th>
                  <th>Task Title</th>
                  <th style={{ width: "130px" }}>Assigned To</th>
                  <th>Status</th>
                  <th>% Done</th>
                  <th style={{ width: '100px' }}>Comments</th>
                  <th style={{ width: "130px" }}>Due Date</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {selectedProject.tasks.map((task) => (
                  <React.Fragment key={task.id}>
                    <tr className="fw-semibold">
                      <td style={{ textAlign: "center" }} data-label="">
                        <Button size="sm" color="link" onClick={() => toggleTaskExpand(task.id)}>
                          {expandedTasks[task.id] ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </Button>
                      </td>
                      <td data-label="Task Title">{task.title}</td>
                      <td data-label="Assigned To">{task.assignedTo}</td>
                      <td data-label="Status" className="text-center align-middle">
                        <span
                          className={`badge rounded-pill px-3 py-2 ${statusColorMap[task.status] || "bg-secondary"
                            }`}
                        >
                          {getStatusLabel(task.status)}
                        </span>
                      </td>
                      <td data-label="% Done" style={{ minWidth: "140px", cursor: "pointer" }} onClick={() => handlePercentClick("task", task.id, task.title)}
                      >
                        <Progress value={task.percentCompleted} color={task.percentCompleted === 100 ? "success" : "info"} style={{ height: "12px", borderRadius: "6px" }} />
                        <small className="text-muted d-block text-center" style={{ cursor: "pointer" }}>
                          {task.percentCompleted}% <span style={{ fontSize: "10px", color: "#007bff" }}>(Click for history)</span>
                        </small>
                      </td>

                      <td data-label="Comments">
                        <div className="d-flex justify-content-center">
                          <Button color="link" size="sm"
                            onClick={() => openComments("task", task.id, task.title)}
                            className="position-relative"
                          >
                            <ChatBubbleOutlineIcon fontSize="small" />

                            {unreadCounts[`task-${task.id}`] > 0 && (

                              <Badge
                                color="danger"
                                className="position-absolute top-0 start-100 translate-middle badge-pulse"
                                style={{
                                  background: "#d32f2f",
                                  color: "white",
                                  padding: "3px 6px",
                                  fontSize: "11px",
                                  borderRadius: "16px",
                                  border: "1.5px solid white",
                                  fontWeight: 600,
                                  boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
                                }}
                              >
                                {unreadCounts[`task-${task.id}`]}
                              </Badge>
                            )}
                          </Button>

                        </div>
                      </td>
                      <td data-label="Due Date">{task.dueDate ? format(new Date(task.dueDate), "dd MMM yyyy") : "-"}</td>

                      <td data-label="Remarks">{task.remarks || "-"}</td>
                    </tr>

                    {expandedTasks[task.id] &&
                      (task.subTasks.length > 0 ? (
                        task.subTasks.map((sub) => (
                          <tr key={sub.id} className="bg-light">
                            <td>↳</td>
                            <td className="text-muted" data-label="Task Title">{sub.title}</td>
                            <td data-label="Assigned To">{sub.assignedToName}</td>
                            <td data-label="Status" className="text-center align-middle">
                              <span className={`badge rounded-pill px-3 py-2 ${statusColorMap[sub.status] || "bg-secondary"}`}>
                                {sub.status}
                              </span>
                            </td>
                            <td data-label="% Done" style={{ minWidth: "140px", cursor: "pointer" }} onClick={() => handlePercentClick("subtask", sub.id, sub.title)}
                            >
                              <Progress value={sub.percentCompleted} color={sub.percentCompleted === 100 ? "success" : "info"} style={{ height: "10px", borderRadius: "5px" }} />
                              <small className="text-muted d-block text-center">{sub.percentCompleted}%</small>
                            </td>

                            <td data-label="Comments">
                              <div className="d-flex justify-content-center">
                                <Button color="link" size="sm"
                                  onClick={() => openComments("subtask", sub.id, sub.title)}
                                  className="position-relative"
                                >
                                  <ChatBubbleOutlineIcon fontSize="small" />

                                  {unreadCounts[`subtask-${sub.id}`] > 0 && (
                                    <Badge
                                      color="danger"
                                      className="position-absolute top-0 start-100 translate-middle badge-pulse"
                                      style={{
                                        background: "#d32f2f",
                                        color: "white",
                                        padding: "3px 6px",
                                        fontSize: "11px",
                                        borderRadius: "16px",
                                        border: "1.5px solid white",
                                        fontWeight: 600,
                                        boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
                                      }}
                                    >
                                      {unreadCounts[`subtask-${sub.id}`]}
                                    </Badge>


                                  )}
                                </Button>

                              </div>

                            </td>
                            <td data-label="Due Date">{sub.dueDate ? format(new Date(sub.dueDate), "dd MMM yyyy") : "-"}</td>

                            <td data-label="Remarks">{sub.remarks || "-"}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-muted fst-italic">
                            No Subtasks
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted text-center">No tasks found for this project.</p>
          )}
        </ModalBody>
      </Modal>

      {/* Progress history modal */}
      <Modal isOpen={chartModalOpen} toggle={() => setChartModalOpen(false)} centered size="xl">
        <ModalHeader toggle={() => setChartModalOpen(false)} className="bg-info text-white">
          <div>
            <div>
              Progress History – {selectedEntity?.type === "task" ? "Task" : "Subtask"} - {selectedEntity?.title && (
                <small style={{ fontSize: "0.9rem", opacity: 0.85 }}>
                  {selectedEntity.title}
                </small>
              )}
            </div>


          </div>
        </ModalHeader>

        <ModalBody >
          {chartLoading ? (
            <div className="text-center py-5"><Spinner color="info" /> Loading history...</div>
          ) : chartData.length === 0 ? (
            <p className="text-center text-muted">No history data available.</p>
          ) : (
            <div style={{ width: "100%", height: 370 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  key={chartData.map(v => v.percentCompleted).join("_")}  // force refresh
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" />
                  <YAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />

                  <Bar
                    dataKey="percentCompleted"
                    fill="#007bff"
                    radius={[6, 6, 0, 0]}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="percentCompleted"
                      position="top"
                      formatter={() => ""}
                    />
                  </Bar>
                </BarChart>


              </ResponsiveContainer>
            </div>
          )}
        </ModalBody>

        <ModalBody style={{ padding: "0" }}>
          {/* TAB HEADERS */}
          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "1" })}
                onClick={() => toggle("1")}
                style={{ cursor: "pointer", fontWeight: "bolder" }}
              >
                All Change Logs
              </NavLink>
            </NavItem>

            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "2" })}
                onClick={() => toggle("2")}
                style={{ cursor: "pointer", fontWeight: "bolder" }}
              >
                Status Change History
              </NavLink>
            </NavItem>
          </Nav>

          {/* TAB CONTENT */}
          <TabContent activeTab={activeTab}>

            {/* TAB 1 - ALL CHANGE LOGS */}
            <TabPane tabId="1">
              <div
                style={{
                  padding: "15px",
                  background: "#f3f4f6",
                  borderRadius: "10px",
                }}
              >

                <div
                  style={{
                    background: "white",
                    borderRadius: "10px",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                  }}
                >
                  <Table hover responsive bordered style={{ marginBottom: 0 }}>
                    <thead
                      style={{
                        background: "#f0f2f5",
                        color: "#333",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      <tr>
                        <th>Field</th>
                        <th>Old Value</th>
                        <th>New Value</th>
                        <th>Action</th>
                        <th>Updated By</th>
                        <th>Date</th>
                      </tr>
                    </thead>

                    <tbody style={{ fontSize: "14px" }}>
                      {overallLogs.map((log, index) => (
                        <tr
                          key={index}
                          style={{
                            background: index % 2 === 0 ? "#ffffff" : "#fafafa",
                            transition: "0.2s",
                          }}
                        >
                          <td style={{ fontWeight: 500 }}>{log.fieldName}</td>
                          <td>{log.oldValue ?? "-"}</td>
                          <td>{log.newValue ?? "-"}</td>

                          {/* Action Badge */}
                          <td>
                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontWeight: 600,
                                fontSize: "12px",
                                textTransform: "capitalize",
                                backgroundColor:
                                  log.action === "Update"
                                    ? "rgba(67, 141, 221, 0.2)"   // light blue
                                    : log.action === "Create"
                                      ? "rgba(22, 226, 70, 0.2)" // light green
                                      : log.action === "Delete"
                                        ? "rgba(218, 72, 86, 0.2)" // light red
                                        : "rgba(108, 117, 125, 0.2)", // fallback grey
                                color:
                                  log.action === "Update"
                                    ? "#0084ffff"   // dark blue text
                                    : log.action === "Create"
                                      ? "#00962aff"   // dark green text
                                      : log.action === "Delete"
                                        ? "#ff001eff"   // dark red text
                                        : "#343a40",  // dark grey
                              }}
                            >
                              {log.action}
                            </span>
                          </td>



                          <td style={{ fontWeight: 500 }}>{log.userName}</td>

                          <td style={{ color: "#555", fontSize: "12px" }}>
                            {new Date(log.logTime).toLocaleDateString("en-GB")}{" "}
                            {new Date(log.logTime).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </TabPane>

            {/* TAB 2 - STATUS CHANGE HISTORY */}
            <TabPane tabId="2">
              <div
                style={{
                  padding: "15px",
                  background: "#f3f4f6",
                  borderRadius: "10px",
                }}
              >
                <h6
                  style={{
                    marginBottom: "15px",
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "#333",
                  }}
                >
                  Status Change History
                </h6>

                <div
                  style={{
                    background: "white",
                    borderRadius: "10px",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                  }}
                >
                  <Table hover responsive bordered style={{ marginBottom: 0 }}>
                    <thead
                      style={{
                        background: "#f0f2f5",
                        color: "#333",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      <tr>
                        <th>Status</th>
                        <th>Updated By</th>
                        <th>Date</th>
                      </tr>
                    </thead>

                    <tbody style={{ fontSize: "14px" }}>
                      {statusHistory.map((item, index) => (
                        <tr
                          key={index}
                          style={{
                            background: index % 2 === 0 ? "#ffffff" : "#fafafa",
                            transition: "0.2s",
                          }}
                        >
                          {/* STATUS CHANGE */}
                          <td>
                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "6px",
                                fontWeight: 500,
                                marginRight: "5px",
                              }}
                            >
                              {item.from}
                            </span>

                            <span style={{ margin: "0 5px", fontWeight: 600 }}>→</span>

                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "6px",
                                fontWeight: 600,
                              }}
                            >
                              {item.to}
                            </span>
                          </td>

                          {/* UPDATED BY */}
                          <td style={{ fontWeight: 500 }}>{item.updatedBy}</td>

                          {/* DATE */}
                          <td style={{ color: "#555", fontSize: "12px" }}>
                            {item.date}, {item.time}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </TabPane>


          </TabContent>
        </ModalBody>


      </Modal>

      {commentPopup?.open && (
        <Comments
          isOpen={commentPopup.open}
          onClose={() => {
            if (commentPopup.type === "task" || commentPopup.type === "subtask") {
              loadUnreadCountFor(commentPopup.type, commentPopup.id);
            }
            setCommentPopup(null);
          }}

          entityType={commentPopup.type}
          entityId={commentPopup.id}
          title={commentPopup.title}
        />
      )}


    </Container>
  );
};

export default GetAllProjectsComponents;
